/* eslint-disable max-statements */
/* eslint-disable no-mixed-spaces-and-tabs */
import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import React from 'react';
import { BsExclamationCircleFill } from 'react-icons/bs';
import { QueryClient, useMutation } from 'react-query';
import { SelectibleTopicCard } from './singleTopic';
import axios from 'axios';
import validator from 'validator';
import { useTypedSelector } from '../../../hooks/useTypeSelector';
import { globalEnv } from '../../../config/baseConfig';

const queryClient = new QueryClient();

type props = {
	userId: string;
	isOpen: boolean;
	setOpen: (isOpen: boolean) => void;
	topics: any[];
	dashboards: any[];
	setDashboards: (dashboards: any) => void;
};

export default function DashboardAddModal({
	userId,
	isOpen = false,
	setOpen,
	topics: topics,
	dashboards,
	setDashboards
}: props) {
	const cancelButtonRef = useRef(null);
	const [isWarning, setIsWarning] = useState(false);
	const [addedTopics, setAddedTopics] = useState([]);
	const [name, setName] = useState('');
	const [isSaving, setIsSaving] = useState(false);

	const { ACCESS_TOKEN } = useTypedSelector((state) => state.auth);

	const save = () => {
		if (addedTopics.length > 0 && name !== '') {
			setIsSaving(true);
			createDashboard(userId);
		} else setIsWarning(true);
	};

	interface subscribeToTopics {
		userId: string;
	}

	const createDashboadReq = async (data: subscribeToTopics) => {
		const dto = {
			userId: data.userId,
			name: name,
			topics: addedTopics.map((topic) => topic)
		};

		const config = {
			headers: {
				Authorization: 'Bearer ' + ACCESS_TOKEN
			}
		};
		const { data: response } = await axios.post(
			globalEnv.BASE_URL + '/admin/dashboard/create',
			dto,
			config
		);
		return response;
	};

	const { mutate } = useMutation(createDashboadReq, {
		onSuccess: (data) => {
			const dash = {
				_id: data._id,
				selectedTopics: data.selectedTopics,
				default: data.default,
				title: data.title
			};
			setDashboards([...dashboards, dash]);
			setOpen(false);
		},
		onError: () => {
			setIsWarning(true);
			setIsSaving(true);
		}
		// onSettled: () => {
		// 	queryClient.invalidateQueries('create');
		// }
	});

	const createDashboard = (userId: string) => {
		mutate({ userId });
	};

	useEffect(() => {
		const timeout = setTimeout(() => {
			setIsWarning(false);
		}, 3000);

		return () => clearTimeout(timeout);
	}, [isWarning]);

	useEffect(() => {
		return () => {
			console.log('Unmount!');
		};
	}, []);

	const Notification = () => {
		return (
			<div className="bg-white rounded-lg border-gray-300 border p-3 shadow-lg cursor-pointer">
				<div className="flex flex-row">
					<div className="mx-auto flex-shrink-0 flex items-center justify-center mt-1 h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
						<BsExclamationCircleFill
							className="h-6 w-6 text-red-600"
							aria-hidden="true"
						/>
					</div>
					<div className="ml-2 mr-6">
						<h3 className="font-semibold">Error!</h3>
						{addedTopics.length === 0 && (
							<h3 className="block text-gray-500 mb-1">
								❌At least one topic has to be selected.
							</h3>
						)}
						{name === '' && (
							<h3 className="block text-gray-500">
								❌Dashboard name is required.
							</h3>
						)}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div>
			<Transition.Root show={isOpen} as={Fragment}>
				<Dialog
					as="div"
					className="fixed z-10 inset-0 pt-5"
					initialFocus={cancelButtonRef}
					onClose={setOpen}
				>
					<div className="flex items-end justify-center min-h-screen text-center sm:block sm:p-0">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0"
							enterTo="opacity-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
						</Transition.Child>

						{/* This element is to trick the browser into centering the modal contents. */}
						<span
							className="hidden sm:inline-block sm:align-middle sm:h-screen"
							aria-hidden="true"
						>
							&#8203;
						</span>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div
								className="relative inline-block bg-white dark:bg-gray-800 rounded-lg text-left
                                       shadow-xl transform transition-all align-middle ml-20 mr-5
                                       max-w-7xl max-h-[80vh] overflow-hidden lg:w-10/12 w-9/12 my-10 sm:my-0"
							>
								<section className="text-gray-700 overflow-scroll max-h-[72vh]">
									<div className="container px-5 sm:px-10 md:px-15 lg:px-20 mx-auto lg:pt-5 pt-3 lg :px-32">
										<h3 className="lg:text-6xl text-4xl font-semibold text-gray-800 dark:text-white text-center">
											Add Dashboard
										</h3>
										<div className="border-t mt-3 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center"></div>

										<div className="mt-5 w-10/12 max-w-lg">
											<h3 className="block mb-2 text-sm font-medium text-gray-800 dark:text-gray-300">
												Dashboard name
											</h3>
											<input
												type="text"
												id="name"
												className="bg-gray-50 border border-gray-500 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
												placeholder="Personal"
												required
												onChange={(e) => setName(e.target.value)}
											/>
										</div>

										<div className="border-t mt-3 border-gray-300 dark:border-gray-700 transition-all duration-500 text-center"></div>

										<h3 className="block mt-5 text-sm font-medium text-gray-800 dark:text-gray-300">
											Selected topics
										</h3>
										<div className="w-full mt-2">
											<div className="carousel p-4 bg-gray-300 dark:bg-gray-700 rounded-box text-center">
												{topics.map((item: any) => (
													<SelectibleTopicCard
														key={item._id}
														id={item._id}
														name={item.name}
														image={item.backgroundImage}
														addedTopics={addedTopics}
														setAddedTopics={setAddedTopics}
													/>
												))}
											</div>
										</div>
									</div>
								</section>

								<div className="bg-white dark:bg-gray-800 px-8 pb-3 sm:px-6 flex flex-row-reverse">
									{addedTopics.length > 0 && !validator.isEmpty(name) && (
										<button
											disabled={isSaving ? true : false}
											type="button"
											className="btn btn-outline btn-sm btn-primary mt-2 ml-1"
											onClick={() => save()}
										>
											save
											{isSaving ? (
												<div className="pl-2">
													<svg
														role="status"
														className="w-5 h-5 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
														viewBox="0 0 100 101"
														fill="none"
														xmlns="http://www.w3.org/2000/svg"
													>
														<path
															d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
															fill="currentColor"
														></path>
														<path
															d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
															fill="currentFill"
														></path>
													</svg>
												</div>
											) : (
												<h5 className="text-xs ml-2">💾</h5>
											)}
										</button>
									)}
									<button
										type="button"
										className="btn btn-outline btn-sm btn-error mt-2 mr-2"
										onClick={() => setOpen(false)}
										ref={cancelButtonRef}
									>
										Cancel
									</button>
								</div>
							</div>
						</Transition.Child>
					</div>
				</Dialog>
			</Transition.Root>
		</div>
	);
}
